/**
 * D254: a clause's second thing is not its first — the one place a phrase is
 * read knowing what the clause has already named.
 *
 * @remarks
 * Found by Mike in the Meridian: the pressure suit and the O2 manifold both
 * answer to `fill line` (the suit's own line, the manifold's four), and
 * `use the suit on the fill line` asked which one he meant. Nobody uses a
 * thing on itself, and a player who names the suit first and then a word the
 * suit shares means the other thing. So a bound phrase — a target, a tool, a
 * destination — that TIES between the clause's direct object and anything
 * else is read again without the direct object. Only a tie: a bound phrase
 * that names the direct object alone (`use the suit on the suit`) still does,
 * and the consumer answers that sentence as it answers any other.
 *
 * D261 is the same reading the other way round. When the bound phrase says
 * where the direct object IS (`take the canister FROM locker m-12`, E-A2-4)
 * and the direct object ties, the bound phrase is read first, and the object
 * again among the things inside what it named. A preference, as D134's is:
 * when nothing inside answers, the first reading's question stands.
 *
 * @packageDocumentation
 */

import type { PhraseToken } from '../grammar/match-clause-structure.js';
import type { ParseContext } from '../parse-context.js';
import type { EntityRef } from '../types.js';

import { type EntityPhraseOutcome, resolveEntityPhrase } from './resolve-entity-phrase.js';

/** How a phrase is read: its tokens, the verb's side of the room, and what the clause's direct object already named. */
export interface RolePhrase {
	readonly objectPreference: Parameters<typeof resolveEntityPhrase>[3];
	/** The direct object's referent, when this is a bound phrase read after it. */
	readonly objectRef?: EntityRef;
	readonly phrase: readonly PhraseToken[];
}

/**
 * Resolve one of a clause's phrases against the catalog — and a bound phrase's tie without the direct object (D254).
 *
 * @param rolePhrase - The phrase and what the clause has named before it
 * @param context - The parse context
 * @param recencyView - Most-recent-first refs, for pronoun heads
 * @returns The phrase's outcome
 */
export const resolveRolePhrase = (rolePhrase: RolePhrase, context: ParseContext, recencyView: readonly EntityRef[]): EntityPhraseOutcome => {
	const { objectPreference, objectRef, phrase } = rolePhrase;
	const readAgainst = (entities: ParseContext['entities']) => resolveEntityPhrase(phrase, entities, recencyView, objectPreference, context.claimedNames);
	const firstReading = readAgainst(context.entities);

	return firstReading.type === 'needs_clarification' && firstReading.candidates.some((candidate) => candidate.entityRef === objectRef)
		? readAgainst(context.entities.filter((entity) => entity.entityRef !== objectRef))
		: firstReading;
};

/**
 * D261: a tied direct object, read again among the things inside the place its clause names.
 *
 * @param objectReading - The direct object's first reading
 * @param objectPhrase - The direct object's phrase and the verb's side of the room
 * @param locatingPhrase - The bound phrase that says where the object is, when the clause has one
 * @param context - The parse context
 * @param recencyView - Most-recent-first refs, for pronoun heads
 * @returns The narrower reading, or the first one when there is no place, it names nothing, or it holds none of them
 */
export const readAmongContents = (
	objectReading: EntityPhraseOutcome,
	objectPhrase: RolePhrase,
	locatingPhrase: undefined | readonly PhraseToken[],
	context: ParseContext,
	recencyView: readonly EntityRef[],
): EntityPhraseOutcome => {
	if (objectReading.type !== 'needs_clarification' || locatingPhrase === undefined) {
		return objectReading;
	}

	const place = resolveRolePhrase({ objectPreference: 'none', phrase: locatingPhrase }, context, recencyView);

	if (place.type !== 'resolved') {
		return objectReading;
	}

	const inside = context.entities.filter((entity) => entity.containerRef === place.resolution.entityRef);
	const insideReading = resolveEntityPhrase(objectPhrase.phrase, inside, recencyView, objectPhrase.objectPreference, context.claimedNames);

	return insideReading.type === 'not_found' ? objectReading : insideReading;
};
